import React from 'react';
import { DollarSign } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from '../../../components/ui/dialog';

export const CreditMemoDetailDialog = ({ open, onOpenChange, creditMemo, onProcessRefund }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="max-w-lg">
      {creditMemo && (
        <>
          <DialogHeader>
            <DialogTitle>Credit Memo {creditMemo.credit_memo_number}</DialogTitle>
            <DialogDescription>For Invoice {creditMemo.invoice_number}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div className="grid grid-cols-2 gap-4">
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="text-xs text-gray-500 uppercase">Credit Amount</p>
                <p className="font-bold text-xl text-red-600">${Math.abs(creditMemo.credit_amount).toFixed(2)}</p>
              </div>
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="text-xs text-gray-500 uppercase">Original Invoice</p>
                <p className="font-bold">${creditMemo.original_invoice_amount?.toFixed(2)}</p>
              </div>
            </div>

            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-500">Client</span>
                <span className="font-medium">{creditMemo.client_name || '—'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Order</span>
                <span className="font-medium">{creditMemo.order_number || '—'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Category</span>
                <Badge variant="outline">{creditMemo.void_category_label}</Badge>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Partial Void</span>
                <span>{creditMemo.is_partial ? 'Yes' : 'No'}</span>
              </div>
              {creditMemo.estimated_production_cost > 0 && (
                <div className="flex justify-between">
                  <span className="text-gray-500">Est. Production Cost</span>
                  <span className="text-amber-600">${creditMemo.estimated_production_cost.toFixed(2)}</span>
                </div>
              )}
            </div>

            <div className="p-3 bg-gray-50 rounded-lg">
              <p className="text-xs text-gray-500 uppercase mb-1">Void Reason</p>
              <p className="text-sm">{creditMemo.void_reason}</p>
            </div>

            {creditMemo.notes && (
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="text-xs text-gray-500 uppercase mb-1">Notes</p>
                <p className="text-sm">{creditMemo.notes}</p>
              </div>
            )}

            {creditMemo.refund_status !== 'not_applicable' && (
              <div className={`p-3 rounded-lg ${creditMemo.refund_status === 'pending_refund' ? 'bg-amber-50 border border-amber-200' : 'bg-green-50 border border-green-200'}`}>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-gray-500 uppercase">Refund Status</p>
                    <Badge className={creditMemo.refund_status === 'pending_refund' ? 'bg-amber-100 text-amber-800' : 'bg-green-100 text-green-800'}>
                      {creditMemo.refund_status === 'pending_refund' ? 'Pending Refund' : 'Refunded'}
                    </Badge>
                    <p className="text-sm mt-1">Amount: ${creditMemo.refund_amount?.toFixed(2)}</p>
                    {creditMemo.refund_method_label && (
                      <p className="text-xs text-gray-500 mt-1">Method: {creditMemo.refund_method_label}</p>
                    )}
                    {creditMemo.refund_reference && (
                      <p className="text-xs text-gray-500">Ref: {creditMemo.refund_reference}</p>
                    )}
                  </div>
                  {creditMemo.refund_status === 'pending_refund' && (
                    <Button size="sm" className="bg-green-600 hover:bg-green-700" onClick={() => onProcessRefund(creditMemo)} data-testid="process-refund-btn">
                      <DollarSign className="h-4 w-4 mr-1" />
                      Process Refund
                    </Button>
                  )}
                </div>
                {creditMemo.refund_processed_at && (
                  <p className="text-xs text-gray-500 mt-2">
                    Processed: {new Date(creditMemo.refund_processed_at).toLocaleString()}
                    {creditMemo.refund_processed_by_email && ` by ${creditMemo.refund_processed_by_email}`}
                  </p>
                )}
              </div>
            )}

            <div className="text-xs text-gray-400 pt-2 border-t">
              <p>Voided by: {creditMemo.voided_by_email}</p>
              <p>Date: {new Date(creditMemo.voided_at).toLocaleString()}</p>
            </div>
          </div>
        </>
      )}
    </DialogContent>
  </Dialog>
);
